import { GenericV1ErrorsArray, useSwrApi } from '@hooks/useSwrApi';

interface UseSwrStartInterviewProps {
  locale?: string;
  slug?: string;
  shouldFetch?: boolean;
  successCallback?: (response: {
    token?: string;
    room_name?: string;
    livekit_url?: string;
  }) => void;
  errorCallback?: (error: GenericV1ErrorsArray) => void;
}

/**
 * @description SWR hook: Get interview session data
 * - `locale` - The current app locale. Default: 'ro'
 * - `slug` - Optional job slug for job-based interview. If not provided, creates CV-based interview
 * - `successCallback` - Function to call on successful data fetch
 * - `errorCallback` - Function to call on error
 */
export const useSwrStartInterview = (props: UseSwrStartInterviewProps) => {
  const {
    locale = 'ro',
    slug,
    shouldFetch,
    successCallback,
    errorCallback,
  } = props;

  // Build the URL - handle both CV-based (no slug) and job-based (with slug) interviews
  const url = slug ? `/v1/interview/start/${slug}` : '/v1/interview/start';

  // SWR hook for GET request
  const {
    data,
    error,
    isLoading,
    isValidating,
    mutate,
  } = useSwrApi<{
    token?: string;
    room_name?: string;
    livekit_url?: string;
  }>({
    url,
    locale,
    shouldFetch,
    apiOptions: {
      // Prevent automatic refetches that could re-enter the interview
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      refreshInterval: 0,
      onSuccess: (response) => {
        if (successCallback) {
          successCallback(response);
        }
      },
      onError: (err) => {
        if (errorCallback) {
          errorCallback(err);
        }
      },
    },
    toastsOptions: {
      showErrorToast: true,
    },
  });

  return {
    data,
    error,
    isLoading,
    isValidating,
    mutate,
  };
};
